import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  DataTable,
  Spinner,
  Banner,
  Stack,
  Text,
  Badge,
  ProgressBar,
  Button,
  EmptyState,
  TextContainer,
  Link
} from '@shopify/polaris';
import { useParams, useNavigate } from 'react-router-dom';
import { useShop } from '../contexts/ShopContext';
import { useTemplate } from '../contexts/TemplateContext';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { getShopImageMetrics } from '../services/shopMetricsService';
import { getCreditUsageStats } from '../services/optimizedCreditService';
import { 
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Legend,
  ComposedChart,
  Area,
  Brush,
  Bar
} from 'recharts';

// Define consistent chart colors
const CHART_COLORS = {
  primary: '#007A5C',    // Base green color
  secondary: '#5C6AC4',  // Complementary blue
  tertiary: '#9C6ADE',   // Purple for third data series
  quaternary: '#F49342', // Orange for fourth data series
  success: '#008060',    // Success green
  warning: '#FFC453',    // Warning yellow
  error: '#DE3618',      // Error red
  brushStroke: '#8884d8' // Brush component color
};

function ShopDetails() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { currentShop } = useShop();
  const { templates } = useTemplate();
  const { user } = useAuth();

  // Helper function to generate credit usage data for the last 12 months
  const generateLast12MonthsDataForCredits = (existingData) => {
    const result = [];
    const now = new Date();
    const existingMonths = {};
    
    // Create a map of existing data by month key
    existingData.forEach(item => {
      existingMonths[item.month] = item;
    });
    
    // Generate data for the last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
      const monthName = date.toLocaleString('default', { month: 'short', year: 'numeric' });
      
      // Use existing data if available, otherwise use zero
      if (existingMonths[monthKey]) {
        result.push(existingMonths[monthKey]);
      } else {
        result.push({
          month: monthKey,
          monthName: monthName,
          credits: 0,
          template: 0,
          image: 0
        });
      }
    }
    
    return result;
  };
  
  // Helper function to generate image processing data for the last 12 months
  const generateLast12MonthsDataForImageProcessing = (existingData) => {
    const result = [];
    const now = new Date();
    const existingMonths = {};
    
    // Create a map of existing data by month key
    existingData.forEach(item => {
      existingMonths[item.month] = item;
    });
    
    // Generate data for the last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
      const monthName = date.toLocaleString('default', { month: 'short', year: 'numeric' });
      
      // Use existing data if available, otherwise use zero
      if (existingMonths[monthKey]) {
        // Add monthName property if it doesn't exist
        if (!existingMonths[monthKey].monthName) {
          existingMonths[monthKey].monthName = monthName;
        }
        result.push(existingMonths[monthKey]);
      } else {
        result.push({
          month: monthKey,
          monthName: monthName,
          imagesProcessed: 0,
          storageSaved: 0,
          compressionRatio: 0
        });
      }
    }
    
    return result;
  };
  
  // Helper function to generate template usage data for the last 12 months
  const generateLast12MonthsDataForTemplates = (existingData) => {
    const result = [];
    const now = new Date();
    const existingMonths = {};
    
    // Create a map of existing data by month key
    existingData.forEach(item => {
      existingMonths[item.month] = item;
    });
    
    // Generate data for the last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
      const monthName = date.toLocaleString('default', { month: 'short', year: 'numeric' });
      
      // Use existing data if available, otherwise use zero
      if (existingMonths[monthKey]) {
        result.push(existingMonths[monthKey]);
      } else {
        result.push({
          month: monthKey,
          monthName: monthName,
          total: 0,
          successful: 0,
          failed: 0,
          successRate: 0
        });
      }
    }
    
    return result;
  };
  
  // Helper function to generate push data for the last 12 months
  const generateLast12MonthsData = (existingData) => {
    const result = [];
    const now = new Date();
    const existingMonths = {};
    
    // Create a map of existing data by month key
    existingData.forEach(item => {
      existingMonths[item.month] = item;
    });
    
    // Generate data for the last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
      const monthName = date.toLocaleString('default', { month: 'short', year: 'numeric' });
      
      // Use existing data if available, otherwise use zero
      if (existingMonths[monthKey]) {
        result.push(existingMonths[monthKey]);
      } else {
        result.push({
          month: monthKey,
          monthName: monthName,
          pushes: 0
        });
      }
    }
    
    return result;
  };
  
  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      // Handle Firestore timestamp objects
      if (timestamp && typeof timestamp.toDate === 'function') {
        return timestamp.toDate().toLocaleDateString();
      }
      
      // Handle raw Unix timestamps (seconds since epoch)
      if (typeof timestamp === 'number') {
        return new Date(timestamp * 1000).toLocaleDateString();
      }
      
      // Handle Date objects
      if (timestamp instanceof Date) {
        return timestamp.toLocaleDateString();
      }
      
      // Handle string timestamps
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
      
      return 'N/A';
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'N/A';
    }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shop, setShop] = useState(null);
  const [shopMetrics, setShopMetrics] = useState({
    creditUsage: {
      total: 0,
      monthly: [],
    },
    pushUsage: {
      total: 0,
      monthly: [],
    },
    templateUsage: {
      total: 0,
      successful: 0,
      failed: 0,
      successRate: '0.0',
      byTemplate: [],
    },
  });
  const [pushMetrics, setPushMetrics] = useState({
    total: 0,
    monthly: []
  });
  const [imageMetrics, setImageMetrics] = useState(null);
  const [creditUsageStats, setCreditUsageStats] = useState(null);

  // Debug logging for credit usage stats and push metrics
  useEffect(() => {
    if (creditUsageStats && creditUsageStats.monthlyTrend) {
      console.log('Monthly credit trend data for chart:', creditUsageStats.monthlyTrend);
    }
    if (creditUsageStats && creditUsageStats.monthlyPushTrend) {
      console.log('Monthly push trend data for chart:', creditUsageStats.monthlyPushTrend);
    }
    if (pushMetrics && pushMetrics.monthly) {
      console.log('Push metrics monthly data:', pushMetrics.monthly);
    }
  }, [creditUsageStats, pushMetrics]);

  useEffect(() => {
    const loadShopMetrics = async () => {
      if (!shopId || !user) return;

      try {
        setLoading(true);
        setError('');
        
        // First try to get shop from Firestore directly
        const shopRef = doc(db, 'shops', shopId);
        const shopDoc = await getDoc(shopRef);

        if (!shopDoc.exists()) {
          setError('Shop not found');
          setLoading(false);
          return;
        }

        const shopData = shopDoc.data();
        
        // Verify user has access to this shop
        if (shopData.userId !== user.uid) {
          setError('Access denied');
          setLoading(false);
          return;
        }

        // Set shop data
        const shopWithId = {
          id: shopDoc.id,
          ...shopData
        };
        
        // Log shop data for debugging
        console.log('Shop data:', shopWithId);
        
        setShop(shopWithId);
        
        // Get metrics from shop document
        const metrics = shopData.metrics || {
          templateUsage: { total: 0, successful: 0, failed: 0, byTemplate: {} },
          creditUsage: { total: 0, monthly: {} },
          pushUsage: { total: 0, monthly: {} }
        };
        
        // Get push metrics from shop document - check both locations where it might be stored
        const pushes = shopData.metrics?.pushes || shopData.pushes || { total: 0, monthly: {} };

        // Format metrics for display
        const formattedMetrics = {
          creditUsage: {
            total: metrics.creditUsage?.total || 0,
            monthly: generateLast12MonthsDataForCredits(Object.entries(metrics.creditUsage?.monthly || {})
              .sort(([a], [b]) => a.localeCompare(b)) // Sort chronologically
              .map(([month, data]) => {
                // Check if data is an object with template/image properties or just a number
                const templateCredits = typeof data === 'object' ? (data.template || 0) : 0;
                const imageCredits = typeof data === 'object' ? (data.image || 0) : 0;
                const totalCredits = typeof data === 'object' ? 
                  (templateCredits + imageCredits) : (parseInt(data) || 0);
                
                // Add monthName for display
                let monthName = 'Unknown';
                try {
                  const [year, monthNum] = month.split('-');
                  const monthDate = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
                  monthName = monthDate.toLocaleString('default', { month: 'short', year: 'numeric' });
                } catch (e) {
                  console.error('Error formatting month for credit usage:', e);
                }
                
                return {
                  month,
                  monthName,
                  credits: totalCredits,
                  template: templateCredits,
                  image: imageCredits
                };
              }))
          },
          pushUsage: {
            total: metrics.pushUsage?.total || 0,
            monthly: Object.entries(metrics.pushUsage?.monthly || {})
              .sort(([a], [b]) => a.localeCompare(b)) // Sort chronologically
              .map(([month, count]) => {
                // Convert YYYY-MM to Month name for display
                const [year, monthNum] = month.split('-');
                const monthDate = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
                const monthName = monthDate.toLocaleString('default', { month: 'short' });
                
                return {
                  month,
                  monthName,
                  pushes: parseInt(count) || 0
                };
              })
          },
          templateUsage: {
            total: metrics.templateUsage?.total || 0,
            successful: metrics.templateUsage?.successful || 0,
            failed: metrics.templateUsage?.failed || 0,
            successRate: metrics.templateUsage?.total > 0 
              ? ((metrics.templateUsage.successful / metrics.templateUsage.total) * 100).toFixed(1)
              : '0.0',
            byTemplate: Object.entries(metrics.templateUsage?.byTemplate || {})
              .map(([templateId, count]) => {
                console.log(`Template ${templateId} has count:`, count, typeof count);
                // Handle count as an object with successful and total properties
                const countValue = typeof count === 'object' ? 
                  (count.total || count.successful || 0) : 
                  (parseInt(count) || 0);
                
                return {
                  templateId,
                  count: countValue
                };
              })
              .sort((a, b) => b.count - a.count) // Sort by usage count descending
              .slice(0, 10), // Top 10 templates
            // Generate monthly template usage data for the chart
            monthly: generateLast12MonthsDataForTemplates(Object.entries(metrics.templateUsage?.monthly || {})
              .map(([month, data]) => {
                // Add monthName for display
                let monthName = 'Unknown';
                try {
                  const [year, monthNum] = month.split('-');
                  const monthDate = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
                  monthName = monthDate.toLocaleString('default', { month: 'short', year: 'numeric' });
                } catch (e) {
                  console.error('Error formatting month for template usage:', e);
                }
                
                return {
                  month,
                  monthName,
                  total: parseInt(data.total || 0),
                  successful: parseInt(data.successful || 0),
                  failed: parseInt(data.failed || 0),
                  successRate: data.total > 0 
                    ? Math.round((data.successful / data.total) * 100) 
                    : 0
                };
              })
              .sort((a, b) => a.month.localeCompare(b.month))) // Sort chronologically
          }
        };

        setShopMetrics(formattedMetrics);

        // Load image processing metrics
        const rawImgMetrics = await getShopImageMetrics(shopId);
        console.log('Raw Image Metrics:', rawImgMetrics);
        
        // Generate historical data for the last 12 months
        if (rawImgMetrics && rawImgMetrics.monthlyData) {
          rawImgMetrics.monthlyData = generateLast12MonthsDataForImageProcessing(rawImgMetrics.monthlyData);
          console.log('Image metrics with historical data:', rawImgMetrics);
        }
        
        setImageMetrics(rawImgMetrics);
        
        // Get credit usage statistics for this shop using the optimized credit service
        try {
          const creditStats = await getCreditUsageStats(user.uid, shopId);
          console.log('Credit usage stats loaded for shop:', creditStats);
          console.log('Raw credit usage stats document for shop:', creditStats);
          console.log('Shop credit usage by type:', creditStats?.usageByType);
          console.log('Shop sizechart credits:', creditStats?.usageByType?.sizechart);
          console.log('Shop byShop data in credit stats:', creditStats?.byShop);
          
          // Make sure the monthlyTrend data has the right structure for the charts
          if (creditStats && creditStats.monthlyTrend) {
            // Ensure we have the monthName property for each month
            creditStats.monthlyTrend = creditStats.monthlyTrend.map(item => {
              if (!item.monthName && item.month) {
                const [year, monthNum] = item.month.split('-');
                const monthDate = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
                item.monthName = monthDate.toLocaleString('default', { month: 'short' });
              }
              return item;
            });
          }
          
          setCreditUsageStats(creditStats);
        } catch (error) {
          console.error('Error loading credit usage statistics:', error);
          // Don't set an error state here, as we still want to show other metrics
        }
        
        // Set push metrics
        let monthlyPushData = [];
        
        // Log the actual push data from Firebase for debugging
        console.log('Push Metrics from Firebase:', pushes);
        console.log('Raw monthly push data:', pushes.monthly);
        
        // Handle monthly push data format
        if (pushes.monthly) {
          // Process monthly data
          monthlyPushData = Object.entries(pushes.monthly || {})
            .sort(([a], [b]) => a.localeCompare(b)) // Sort chronologically
            .map(([month, count]) => {
              // Format month for display (YYYY-MM -> Month Name)
              let monthName = 'Unknown';
              try {
                const [year, monthNum] = month.split('-');
                const monthDate = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
                monthName = monthDate.toLocaleString('default', { month: 'short', year: 'numeric' });
              } catch (e) {
                console.error('Error formatting month:', e);
              }
              
              return {
                month,
                monthName,
                pushes: typeof count === 'number' ? count : parseInt(count) || 0
              };
            });
        }
        
        // If we still don't have data, check if we have daily data to aggregate by month
        if (monthlyPushData.length === 0 && pushes.daily) {
          const monthlyAggregated = {};
          
          // Aggregate daily data by month
          Object.entries(pushes.daily).forEach(([day, count]) => {
            // Extract month from day (YYYY-MM-DD -> YYYY-MM)
            const month = day.substring(0, 7);
            monthlyAggregated[month] = (monthlyAggregated[month] || 0) + parseInt(count || 0);
          });
          
          // Convert to array format
          monthlyPushData = Object.entries(monthlyAggregated)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([month, count]) => {
              // Format month for display (YYYY-MM -> Month Name)
              let monthName = 'Unknown';
              try {
                const [year, monthNum] = month.split('-');
                const monthDate = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
                monthName = monthDate.toLocaleString('default', { month: 'short', year: 'numeric' });
              } catch (e) {
                console.error('Error formatting month:', e);
              }
              
              return {
                month,
                monthName,
                pushes: count
              };
            });
        }
        
        // Generate data for the last 12 months to ensure we have a good chart view
        const last12Months = generateLast12MonthsData(monthlyPushData);
        
        const formattedPushMetrics = {
          total: pushes.total || 0,
          monthly: last12Months
        };
        
        console.log('Formatted Push Metrics:', formattedPushMetrics);
        console.log('Monthly push data with formatted dates:', monthlyPushData);
        setPushMetrics(formattedPushMetrics);

      } catch (error) {
        console.error('Error loading shop metrics:', error);
        setError('Failed to load shop metrics');
      } finally {
        setLoading(false);
      }
    };

    loadShopMetrics();
  }, [shopId, user]);

  if (loading) {
    return (
      <Page title="Shop Details">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  if (!shop) {
    return (
      <Page title="Shop Details">
        <EmptyState
          heading="Shop not found"
          action={{
            content: 'Back to Shops',
            onAction: () => navigate('/app/shops')
          }}
        >
          <p>The shop you're looking for doesn't exist.</p>
        </EmptyState>
      </Page>
    );
  }

  const isActive = currentShop?.id === shop.id;

  return (
    <Page
      title={shop.name}
      breadcrumbs={[{ content: 'Shops', onAction: () => navigate('/app/shops') }]}
      subtitle={
        <Badge status={isActive ? 'success' : 'critical'}>
          {isActive ? 'Active' : 'Inactive'}
        </Badge>
      }
    >
      {error && (
        <Banner status="critical" onDismiss={() => setError('')}>
          <p>{error}</p>
        </Banner>
      )}

      <Layout>
        {/* SECTION 1: SHOP INFORMATION */}
        <Layout.Section>
          <TextContainer>
            <Text variant="headingMd" as="h2">Shop Information</Text>
            <Text variant="bodySm" as="p">Details about this Shopify store and its connection status.</Text>
          </TextContainer>
          <Card>
            <Card.Section>
              <Stack vertical spacing="loose">
                <Stack distribution="fillEvenly">
                  <Stack vertical spacing="extraTight">
                    <Text variant="bodySm" as="p" color="subdued">
                      Shop URL
                    </Text>
                    <Text variant="bodyMd" as="p">
                      {shop.shop ? (
                        <Link url={`https://${shop.shop}`} external>
                          {shop.shop}
                        </Link>
                      ) : shop.myshopifyDomain ? (
                        <Link url={`https://${shop.myshopifyDomain}`} external>
                          {shop.myshopifyDomain}
                        </Link>
                      ) : shop.domain ? (
                        <Link url={`https://${shop.domain}`} external>
                          {shop.domain}
                        </Link>
                      ) : shop.url ? (
                        <Link url={shop.url.startsWith('http') ? shop.url : `https://${shop.url}`} external>
                          {shop.url}
                        </Link>
                      ) : (
                        'Not available'
                      )}
                    </Text>
                  </Stack>
                  
                  <Stack vertical spacing="extraTight">
                    <Text variant="bodySm" as="p" color="subdued">
                      Connected On
                    </Text>
                    <Text variant="bodyMd" as="p">
                      {formatDate(shop.createdAt)}
                    </Text>
                  </Stack>
                  
                  <Stack vertical spacing="extraTight">
                    <Text variant="bodySm" as="p" color="subdued">
                      Status
                    </Text>
                    <Badge status={isActive ? 'success' : 'critical'}>
                      {isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </Stack>
                </Stack>
              </Stack>
            </Card.Section>
          </Card>
        </Layout.Section>

        {/* SECTION 2: SHOPIFY PUSH STATS */}
        <Layout.Section>
          <TextContainer>
            <Text variant="headingMd" as="h2">Shopify Push Statistics</Text>
            <Text variant="bodySm" as="p">Product push metrics and trends for this store.</Text>
          </TextContainer>
          <Card>
            <Card.Section title="Overview">
              <Stack distribution="fillEvenly" alignment="center">
                <Stack vertical spacing="extraTight" alignment="center">
                  <Text variant="headingLg" as="h3">
                    {pushMetrics.total}
                  </Text>
                  <Text variant="bodyMd" as="p">
                    Total Products Pushed
                  </Text>
                </Stack>
                
                {pushMetrics.monthly && pushMetrics.monthly.length > 0 && (
                  <Stack vertical spacing="extraTight" alignment="center">
                    <Text variant="headingLg" as="h3">
                      {pushMetrics.monthly[pushMetrics.monthly.length - 1].pushes}
                    </Text>
                    <Text variant="bodyMd" as="p">
                      Pushes This Month
                    </Text>
                  </Stack>
                )}
                
                {/* Add a third metric for balance */}
                <Stack vertical spacing="extraTight" alignment="center">
                  <Text variant="headingLg" as="h3">
                    {Math.round(pushMetrics.total / (imageMetrics?.totalStats?.imagesProcessed || 1) * 100)}%
                  </Text>
                  <Text variant="bodyMd" as="p">
                    Push Rate
                  </Text>
                </Stack>
              </Stack>
            </Card.Section>
            
            <Card.Section title="Monthly Push Trend">
                <div style={{ width: '100%', height: 300 }}>
                  {/* Add date range information */}
                  {pushMetrics.monthly && pushMetrics.monthly.length > 0 && (
                    <Text variant="bodySm" as="p" color="subdued" alignment="center" style={{ marginBottom: '10px' }}>
                      {pushMetrics.monthly[0].monthName && pushMetrics.monthly[pushMetrics.monthly.length - 1].monthName ? 
                        `Showing data from ${pushMetrics.monthly[0].monthName} to ${pushMetrics.monthly[pushMetrics.monthly.length - 1].monthName}` :
                        `Showing ${pushMetrics.monthly.length} month${pushMetrics.monthly.length > 1 ? 's' : ''} of data`}
                    </Text>
                  )}
                  <ResponsiveContainer>
                    <ComposedChart
                      data={creditUsageStats?.monthlyPushTrend || pushMetrics.monthly || []}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="monthName" 
                        tickFormatter={(value) => {
                          // Just return the month name directly since it's already formatted
                          return value;
                        }}
                      />
                      <YAxis yAxisId="left" orientation="left" stroke={CHART_COLORS.primary} />
                      <Tooltip 
                        labelFormatter={(value) => {
                          // The month name is already formatted, just return it
                          return value;
                        }}
                        formatter={(value) => [`${value} pushes`, 'Product Pushes']}
                      />
                      <Legend />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="pushes"
                        name="Product Pushes"
                        fill={CHART_COLORS.primary}
                        stroke={CHART_COLORS.primary}
                        fillOpacity={0.2}
                      />
                      <Brush dataKey="monthName" height={30} stroke={CHART_COLORS.brushStroke} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </Card.Section>
          </Card>
        </Layout.Section>

        {/* SECTION 4: CREDIT USAGE STATS */}
        <Layout.Section>
          <TextContainer>
            <Text variant="headingMd" as="h2">Credit Usage Statistics</Text>
            <Text variant="bodySm" as="p">Credit consumption metrics and trends for this store.</Text>
          </TextContainer>
          <Card>
            {creditUsageStats ? (
              <>
                <Card.Section title="Credit Usage Overview">
                  <Stack distribution="fillEvenly" alignment="center">
                    <Stack vertical alignment="center">
                      <Text variant="headingLg" as="h3">
                        {creditUsageStats.usageByType.template || 0}
                      </Text>
                      <Text variant="bodySm" as="p">
                        Template Credits
                      </Text>
                      <Text variant="bodySm" as="p" color="subdued">
                        {creditUsageStats.usagePercentByType.template || 0}%
                      </Text>
                    </Stack>
                    <Stack vertical alignment="center">
                      <Text variant="headingLg" as="h3">
                        {creditUsageStats.usageByType.image || 0}
                      </Text>
                      <Text variant="bodySm" as="p">
                        Image Credits
                      </Text>
                      <Text variant="bodySm" as="p" color="subdued">
                        {creditUsageStats.usagePercentByType.image || 0}%
                      </Text>
                    </Stack>
                    <Stack vertical alignment="center">
                      <Text variant="headingLg" as="h3">
                        {creditUsageStats.usageByType.sizechart || 0}
                      </Text>
                      <Text variant="bodySm" as="p">
                        Sizechart Credits
                      </Text>
                      <Text variant="bodySm" as="p" color="subdued">
                        {creditUsageStats.usagePercentByType.sizechart || 0}%
                      </Text>
                    </Stack>
                    <Stack vertical alignment="center">
                      <Text variant="headingLg" as="h3">
                        {creditUsageStats.totalUsage || 0}
                      </Text>
                      <Text variant="bodySm" as="p">
                        Total Credits Used
                      </Text>
                    </Stack>
                  </Stack>
                </Card.Section>
                
                <Card.Section title="Credit Usage Distribution">
                  <div style={{ width: '100%', height: 250 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Template', value: creditUsageStats.usageByType.template || 0, fill: CHART_COLORS.primary },
                            { name: 'Image', value: creditUsageStats.usageByType.image || 0, fill: CHART_COLORS.secondary },
                            { name: 'Sizechart', value: creditUsageStats.usageByType.sizechart || 0, fill: '#4F8A10' }
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [value, `${name} Credits`]}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </Card.Section>
                
                {creditUsageStats && creditUsageStats.monthlyTrend && creditUsageStats.monthlyTrend.length > 0 && (
                  <Card.Section title="Monthly Credit Usage Trend">
                    <div style={{ width: '100%', height: 300 }}>
                      <ResponsiveContainer>
                        <ComposedChart
                          data={creditUsageStats.monthlyTrend}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="monthName" 
                            tickFormatter={(value) => {
                              // Just return the month name directly since it's already formatted
                              return value;
                            }}
                          />
                          <YAxis yAxisId="left" orientation="left" stroke={CHART_COLORS.primary} />
                          <YAxis yAxisId="right" orientation="right" stroke={CHART_COLORS.secondary} />
                          <Tooltip 
                            labelFormatter={(value) => {
                              // The month name is already formatted, just return it
                              return value;
                            }}
                            formatter={(value, name) => {
                              if (name === 'Template Credits') return [value, name];
                              if (name === 'Image Credits') return [value, name];
                              return [value, name];
                            }}
                          />
                          <Legend />
                          <Area
                            type="monotone"
                            dataKey="credits"
                            name="Total Credits"
                            fill={CHART_COLORS.primary}
                            stroke="#008060"
                            fillOpacity={0.2}
                            yAxisId="left"
                          />
                          <Line
                            type="monotone"
                            dataKey="template"
                            name="Template Credits"
                            stroke={CHART_COLORS.secondary}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 8 }}
                            yAxisId="right"
                          />
                          <Line
                            type="monotone"
                            dataKey="image"
                            name="Image Credits"
                            stroke="#9c6ade"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 8 }}
                            yAxisId="right"
                          />
                          <Line
                            type="monotone"
                            dataKey="sizechart"
                            name="Sizechart Credits"
                            stroke="#4F8A10"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 8 }}
                            yAxisId="right"
                          />
                          <Brush dataKey="monthName" height={30} stroke={CHART_COLORS.brushStroke} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </Card.Section>
                )}
              </>
            ) : (
              <Card.Section>
                <Stack vertical spacing="tight">
                  <Text variant="headingMd" as="h3">
                    Total Credits Used: {shopMetrics.creditUsage.total}
                  </Text>
                  <div style={{ height: '250px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={shopMetrics.creditUsage.monthly}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="credits"
                          stroke="#5c6ac4"
                          name="Credits Used"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Stack>
              </Card.Section>
            )}
          </Card>
        </Layout.Section>

        {/* SECTION 3: IMAGE PROCESSING STATS */}
        <Layout.Section>
          <TextContainer>
            <Text variant="headingMd" as="h2">Image Processing Statistics</Text>
            <Text variant="bodySm" as="p">Image optimization metrics and trends for this store.</Text>
          </TextContainer>
          <Card>
            {imageMetrics ? (
              <>
                <Card.Section title="Overview">
                  <Stack distribution="fillEvenly">
                    <Stack vertical spacing="extraTight" alignment="center">
                      <Text variant="headingLg" as="h3">
                        {imageMetrics.totalStats.imagesProcessed}
                      </Text>
                      <Text variant="bodyMd" as="p">
                        Images Processed
                      </Text>
                    </Stack>

                    <Stack vertical spacing="extraTight" alignment="center">
                      <Text variant="headingLg" as="h3">
                        {Math.round(imageMetrics.totalStats.storageSaved / (1024 * 1024))} MB
                      </Text>
                      <Text variant="bodyMd" as="p">
                        Storage Saved
                      </Text>
                    </Stack>

                    <Stack vertical spacing="extraTight" alignment="center">
                      <Text variant="headingLg" as="h3">
                        {Number(imageMetrics.totalStats.compressionRatio).toFixed(1)}%
                      </Text>
                      <Text variant="bodyMd" as="p">
                        Avg. Compression
                      </Text>
                    </Stack>
                  </Stack>
                  
                  {imageMetrics.totalStats.lastUpdate && (
                    <Text variant="bodySm" color="subdued" alignment="center">
                      Last updated: {formatDate(imageMetrics.totalStats.lastUpdate)}
                    </Text>
                  )}
                </Card.Section>
                
                {/* Monthly Image Processing Chart */}
                {imageMetrics.monthlyData && imageMetrics.monthlyData.length > 0 && (
                  <Card.Section title="Monthly Processing Activity">
                    <div style={{ width: '100%', height: 300 }}>
                      {/* Add date range information */}
                      {imageMetrics.monthlyData && imageMetrics.monthlyData.length > 0 && (
                        <Text variant="bodySm" as="p" color="subdued" alignment="center" style={{ marginBottom: '10px' }}>
                          {imageMetrics.monthlyData[0].monthName && imageMetrics.monthlyData[imageMetrics.monthlyData.length - 1].monthName ? 
                            `Showing data from ${imageMetrics.monthlyData[0].monthName} to ${imageMetrics.monthlyData[imageMetrics.monthlyData.length - 1].monthName}` :
                            `Showing ${imageMetrics.monthlyData.length} month${imageMetrics.monthlyData.length > 1 ? 's' : ''} of data`}
                        </Text>
                      )}
                      <ResponsiveContainer>
                        <ComposedChart
                          data={imageMetrics.monthlyData}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="monthName" 
                            tickFormatter={(value) => {
                              // Just return the month name directly since it's already formatted
                              return value;
                            }}
                          />
                          <YAxis yAxisId="left" orientation="left" stroke={CHART_COLORS.primary} />
                          <YAxis yAxisId="right" orientation="right" stroke={CHART_COLORS.secondary} />
                          <Tooltip 
                            labelFormatter={(value) => {
                              // The month name is already formatted, just return it
                              return value;
                            }}
                            formatter={(value, name) => {
                              if (name === 'Storage Saved (MB)') {
                                return [Math.round(value), name];
                              }
                              return [value, name];
                            }}
                          />
                          <Legend />
                          <Bar
                            yAxisId="left"
                            dataKey="imagesProcessed"
                            name="Images Processed"
                            fill={CHART_COLORS.primary}
                            barSize={20}
                            radius={[4, 4, 0, 0]}
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="storageSaved"
                            name="Storage Saved (MB)"
                            stroke={CHART_COLORS.secondary}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 8 }}
                          />
                          <Brush dataKey="monthName" height={30} stroke={CHART_COLORS.brushStroke} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </Card.Section>
                )}
                
                {/* Daily Image Processing Chart */}
                {imageMetrics.dailyData && imageMetrics.dailyData.length > 1 && (
                  <Card.Section title="Daily Processing Activity">
                    <div style={{ width: '100%', height: 300 }}>
                      <ResponsiveContainer>
                        <ComposedChart
                          data={imageMetrics.dailyData}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="date" 
                            tickFormatter={(value) => {
                              // Convert YYYY-MM-DD to MM-DD format
                              try {
                                const date = new Date(value);
                                return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                              } catch (e) {
                                return value;
                              }
                            }}
                          />
                          <YAxis yAxisId="left" orientation="left" stroke={CHART_COLORS.primary} />
                          <YAxis yAxisId="right" orientation="right" stroke={CHART_COLORS.secondary} />
                          <Tooltip 
                            labelFormatter={(value) => {
                              // Convert YYYY-MM-DD to readable format
                              try {
                                const date = new Date(value);
                                return date.toLocaleDateString('default', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                              } catch (e) {
                                return `Date: ${value}`;
                              }
                            }}
                            formatter={(value, name) => {
                              if (name === 'Storage Saved (MB)') {
                                return [Math.round(value), name];
                              }
                              return [value, name];
                            }}
                          />
                          <Legend />
                          <Bar
                            yAxisId="left"
                            dataKey="imagesProcessed"
                            name="Images Processed"
                            fill={CHART_COLORS.primary}
                            barSize={20}
                            radius={[4, 4, 0, 0]}
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="storageSaved"
                            name="Storage Saved (MB)"
                            stroke={CHART_COLORS.secondary}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 8 }}
                          />
                          <Brush dataKey="date" height={30} stroke="#8884d8" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </Card.Section>
                )}
              </>
            ) : (
              <Card.Section>
                <div style={{ textAlign: 'center', padding: '1rem' }}>
                  <Spinner />
                </div>
              </Card.Section>
            )}
          </Card>
        </Layout.Section>

        {/* SECTION 5: TEMPLATE USAGE */}
        <Layout.Section>
          <TextContainer>
            <Text variant="headingMd" as="h2">Template Usage</Text>
            <Text variant="bodySm" as="p">Details about template usage for this Shopify store.</Text>
          </TextContainer>
          <Card>
            <Card.Section title="Overview">
              <Stack vertical spacing="loose">
                <Stack distribution="fillEvenly" alignment="center">
                  <Stack vertical spacing="extraTight" alignment="center">
                    <Text variant="headingLg" as="h3">
                      {shopMetrics.templateUsage.total}
                    </Text>
                    <Text variant="bodyMd" as="p">
                      Total Templates Used
                    </Text>
                  </Stack>
                  
                  <Stack vertical spacing="extraTight" alignment="center">
                    <Text variant="headingLg" as="h3">
                      {shopMetrics.templateUsage.successful}
                    </Text>
                    <Text variant="bodyMd" as="p">
                      Successful
                    </Text>
                  </Stack>
                  
                  <Stack vertical spacing="extraTight" alignment="center">
                    <Text variant="headingLg" as="h3">
                      {shopMetrics.templateUsage.successRate}%
                    </Text>
                    <Text variant="bodyMd" as="p">
                      Success Rate
                    </Text>
                  </Stack>
                </Stack>
                
                {/* Monthly Template Usage Chart */}
                {shopMetrics.templateUsage.monthly && shopMetrics.templateUsage.monthly.length > 0 && (
                  <div style={{ width: '100%', height: 250, marginTop: '20px' }}>
                    <Text variant="headingSm" as="h4" style={{ marginBottom: '10px', textAlign: 'center' }}>
                      Monthly Template Usage
                    </Text>
                    <ResponsiveContainer>
                      <ComposedChart
                        data={shopMetrics.templateUsage.monthly}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="monthName" 
                          tickFormatter={(value) => {
                            // Just return the month name directly since it's already formatted
                            return value;
                          }}
                        />
                        <YAxis yAxisId="left" orientation="left" stroke="#007A5C" />
                        <YAxis yAxisId="right" orientation="right" stroke="#5C6AC4" />
                        <Tooltip 
                          labelFormatter={(value) => {
                            // Convert YYYY-MM to Month Year format
                            try {
                              const date = new Date(value + '-01');
                              return date.toLocaleString('default', { month: 'long', year: 'numeric' });
                            } catch (e) {
                              return `Month: ${value}`;
                            }
                          }}
                        />
                        <Legend />
                        <Bar
                          yAxisId="left"
                          dataKey="total"
                          name="Total Uses"
                          fill={CHART_COLORS.primary}
                          barSize={20}
                          radius={[4, 4, 0, 0]}
                        />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="successRate"
                          name="Success Rate (%)"
                          stroke={CHART_COLORS.secondary}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 8 }}
                        />
                        <Brush dataKey="monthName" height={30} stroke={CHART_COLORS.brushStroke} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Stack>
            </Card.Section>
            
            <Card.Section title="Template Breakdown">
              <Stack vertical spacing="loose">
                {shopMetrics.templateUsage.byTemplate.length > 0 ? (
                  shopMetrics.templateUsage.byTemplate.map(template => {
                    const templateData = templates.find(t => t.id === template.templateId);
                    if (!templateData) return null;
                    
                    // Log template data for debugging
                    console.log('Template data:', templateData.name, 'Count:', template.count);

                    return (
                      <Stack key={template.templateId} distribution="equalSpacing" alignment="center">
                        <Stack.Item>
                          <Stack spacing="tight">
                            <Text variant="bodyMd" as="span">
                              {templateData.name}
                            </Text>
                            <Text variant="bodySm" as="span" color="subdued">
                              ({templateData.type})
                            </Text>
                          </Stack>
                        </Stack.Item>
                        <Stack.Item>
                          <Stack spacing="tight" alignment="center">
                            <Text variant="bodyMd" as="span">
                              {template.count || 0} uses
                            </Text>
                            <div style={{ width: '100px' }}>
                              <ProgressBar
                                progress={shopMetrics.templateUsage.total > 0 ? ((template.count || 0) / shopMetrics.templateUsage.total) * 100 : 0}
                                size="small"
                                color="primary"
                              />
                            </div>
                          </Stack>
                        </Stack.Item>
                      </Stack>
                    );
                  })
                ) : (
                  <Text variant="bodyMd" as="p" alignment="center" color="subdued">
                    No template usage data available for this shop.
                  </Text>
                )}
              </Stack>
            </Card.Section>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default ShopDetails;
